/**
 * The folded-in website sheets on the one Audit page — the retired standalone
 * Website Audit page's table stack (filter bar, chips, table, pagination),
 * pinned to the run's auto-chained website run and to the tab the operator
 * picked. Owns its own WebsiteAuditProvider: the website data is a different
 * run with different sheets, so it keeps its own context exactly as the old
 * page did.
 */
import React, { useEffect } from "react"

import { AuditRunSummary } from "../../api/audit"
import {
  WebsiteAuditProvider,
  WebsiteSheetSlug,
  useWebsiteAudit,
} from "../../context/WebsiteAuditContext"
import RunWebsiteAuditButton from "../website-audit/RunWebsiteAuditButton"
import WebsiteAuditTable from "../website-audit/WebsiteAuditTable"
import WebsiteDownloadMenu from "../website-audit/WebsiteDownloadMenu"
import WebsiteFilterBar from "../website-audit/WebsiteFilterBar"
import WebsiteFilterChips from "../website-audit/WebsiteFilterChips"
import WebsitePaginationFooter from "../website-audit/WebsitePaginationFooter"
import { THEME } from "../../config/theme"

interface Props {
  /** The chained website run. */
  run: AuditRunSummary
  sheet: WebsiteSheetSlug
  dealerGroupId: number
  /** Render the Re-check action (latest run only) and refresh on completion. */
  showRecheck: boolean
  onRecheckComplete: () => void
}

function WebsiteSheetContent({ run, sheet, dealerGroupId, showRecheck, onRecheckComplete }: Props) {
  const runId = run.id
  const {
    currentSheet,
    setCurrentRunId,
    setCurrentSheet,
    loadColumnsForSheet,
    setPage,
    loadVehicles,
  } = useWebsiteAudit()

  // Pin the context to the chained run + the selected tab (the standalone
  // page had its own selector; here the Audit page decides both).
  useEffect(() => {
    setCurrentRunId(runId)
  }, [runId, setCurrentRunId])
  useEffect(() => {
    setCurrentSheet(sheet)
  }, [sheet, setCurrentSheet])

  // Same load choreography as the retired page.
  useEffect(() => {
    loadColumnsForSheet(currentSheet)
  }, [currentSheet, loadColumnsForSheet])
  useEffect(() => {
    setPage(1)
  }, [currentSheet, setPage])
  useEffect(() => {
    loadVehicles()
  }, [loadVehicles])

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: THEME.spacing.sm,
          marginBottom: THEME.spacing.sm,
        }}
      >
        {/* Re-scrape the site against the latest audit without re-running the
            whole audit — the one standalone-page action worth keeping. */}
        {showRecheck && (
          <RunWebsiteAuditButton dealerGroupId={dealerGroupId} onComplete={onRecheckComplete} />
        )}
        <WebsiteDownloadMenu currentRun={run} />
      </div>
      <WebsiteFilterBar />
      <WebsiteFilterChips />
      <WebsiteAuditTable />
      <WebsitePaginationFooter />
    </>
  )
}

export default function WebsiteSheetSection(props: Props) {
  return (
    <WebsiteAuditProvider>
      <WebsiteSheetContent {...props} />
    </WebsiteAuditProvider>
  )
}
